import React from 'react';
import { Typography, Spin, Tag, Button, Tooltip } from 'antd';
import { CalendarOutlined, FlagOutlined, EditOutlined, InfoCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

const { Text, Paragraph } = Typography;

/**
 * Kampanya listesi bileşeni
 * Belirli tarih aralığındaki tüm kampanyaları listeler
 */
const CampaignsList = ({ brandId, selectedMonth, allCampaigns = [], onEdit }) => {
  // Kampanya renklerini belirleme - her kampanya için farklı bir renk
  const campaignColors = [
    '#FF5722', // Turuncu
    '#2196F3', // Mavi
    '#4CAF50', // Yeşil
    '#9C27B0', // Mor
    '#E91E63', // Pembe
    '#00BCD4', // Açık Mavi
    '#8BC34A', // Açık Yeşil
    '#FFC107', // Sarı
    '#795548', // Kahverengi
    '#607D8B'  // Gri Mavi
  ];

  // Kampanya adına göre renk döndüren fonksiyon
  const getCampaignColor = (campaignName) => {
    if (!campaignName) return '#000000';
    
    // Kampanya adının toplam karakter değerine göre renk seçimi
    let sum = 0;
    for (let i = 0; i < campaignName.length; i++) {
      sum += campaignName.charCodeAt(i);
    }
    
    // Mod işlemi ile renk dizisinden seçim yapılıyor
    return campaignColors[sum % campaignColors.length];
  };

  // Kampanya tarihi ile ay arasındaki ilerleme yüzdesini hesapla
  const calculateProgress = (campaign) => {
    const monthStart = selectedMonth.startOf('month');
    const monthEnd = selectedMonth.endOf('month');
    const campaignStart = dayjs(campaign.startDate);
    const campaignEnd = dayjs(campaign.endDate);
    
    // Kampanyanın ay içindeki başlangıç ve bitiş noktalarını belirle
    const effectiveStart = campaignStart.isAfter(monthStart) ? campaignStart : monthStart;
    const effectiveEnd = campaignEnd.isBefore(monthEnd) ? campaignEnd : monthEnd;
    
    // Ay içindeki süre
    const daysInMonth = monthEnd.diff(monthStart, 'day');
    
    // Kampanyanın ay başından itibaren kaç gün sonra başladığı
    const daysUntilStart = effectiveStart.diff(monthStart, 'day');
    
    // Kampanyanın ay içinde kaç gün sürdüğü
    const campaignDaysInMonth = effectiveEnd.diff(effectiveStart, 'day') + 1;
    
    // Başlangıç yüzdesi
    const startPercent = (daysUntilStart / daysInMonth) * 100;
    
    // Süre yüzdesi
    const durationPercent = (campaignDaysInMonth / daysInMonth) * 100;
    
    return {
      startPercent: Math.max(0, Math.min(100, startPercent)),
      durationPercent: Math.max(0, Math.min(100, durationPercent))
    };
  };

  // Tarih formatını düzenle
  const formatDate = (dateString) => {
    return dayjs(dateString).format('DD MMM YYYY');
  };

  // Kampanya durumunu belirle (Başlayacak, Devam Ediyor, Tamamlandı)
  const getCampaignStatus = (campaign) => {
    const now = dayjs();
    const campaignStart = dayjs(campaign.startDate);
    const campaignEnd = dayjs(campaign.endDate);
    
    if (campaignStart.isAfter(now)) {
      return { text: 'Başlayacak', color: '#722ed1' }; // Mor
    } else if (campaignEnd.isBefore(now)) {
      return { text: 'Tamamlandı', color: '#1890ff' }; // Mavi
    } else {
      return { text: 'Devam Ediyor', color: '#52c41a' }; // Yeşil
    }
  };

  // Marka ID'sine göre kampanyaları filtrele
  const filteredCampaigns = brandId 
    ? allCampaigns.filter(campaign => campaign.Brand && campaign.Brand.id === brandId)
    : allCampaigns;
    
  // Kampanyaları başlangıç tarihine göre eskiden yeniye doğru sırala
  const sortedCampaigns = [...filteredCampaigns].sort((a, b) => {
    const dateA = new Date(a.startDate);
    const dateB = new Date(b.startDate);
    return dateA - dateB; // Eskiden yeniye doğru sıralama
  });

  // Kampanya yoksa
  if (sortedCampaigns.length === 0) {
    return (
      <div style={{ marginTop: 8 }}>
        <Text type="secondary" italic>Bu ay için kampanya bulunamadı.</Text>
      </div>
    );
  }

  return (
    <div style={{ marginTop: 16, marginBottom: 16 }}>
      <Text style={{ fontSize: '14px', fontWeight: 'bold', display: 'block', marginBottom: 8 }}>
        <FlagOutlined /> Kampanyalar ({sortedCampaigns.length})
      </Text>
      
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
        {sortedCampaigns.map(campaign => {
          const campaignColor = getCampaignColor(campaign.name);
          const status = getCampaignStatus(campaign);
          
          return (
            <div 
              key={campaign.id}
              style={{ 
                border: `1px solid #f0f0f0`,
                borderLeft: `3px solid ${campaignColor}`,
                padding: '8px 10px',
                borderRadius: '4px',
                backgroundColor: '#fff',
                flex: '1 1 auto',
                minWidth: '200px',
                maxWidth: '250px',
                position: 'relative'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text style={{ fontSize: '13px', fontWeight: 'bold' }}>
                  {campaign.description ? (
                    <Tooltip title={campaign.description}>
                      <span>{campaign.name} <InfoCircleOutlined style={{ fontSize: '12px', opacity: 0.6 }} /></span>
                    </Tooltip>
                  ) : (
                    campaign.name
                  )}
                </Text>
                <Tag color={status.color} style={{ fontSize: '11px', margin: 0, padding: '0 5px', height: '18px', lineHeight: '18px' }}>
                  {status.text}
                </Tag>
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: '11px' }}>
                  <CalendarOutlined /> {formatDate(campaign.startDate)} - {formatDate(campaign.endDate)}
                </Text>
              </div>
              
              {/* Düzenle butonu */}
              {onEdit && (
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  size="small"
                  style={{ 
                    position: 'absolute', 
                    right: '5px', 
                    bottom: '5px', 
                    padding: '0 4px',
                    opacity: 0.5
                  }}
                  onClick={() => onEdit(campaign)}
                  title="Düzenle"
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CampaignsList; 